import { useDictionaryContext } from 'application/contexts/DictionaryContext';
import { usePageContext } from 'application/contexts/PageContext';
import { useSiteContext } from 'application/contexts/SiteContext';
import { NavigationItemProps } from 'designsystem/components/3-organisms/Navigation/NavigationItem';
import { Header } from 'designsystem/components/4-habitats/Header';
import { HeaderSimple } from 'designsystem/components/4-habitats/HeaderSimple';
import { HeaderTop } from 'designsystem/components/4-habitats/HeaderTop';
import { LinkFeature } from 'features/Modules/Link';
import { LoginFeature } from 'features/Modules/Login';
import { LogoFeature } from 'features/Modules/Logo';
import { SearchModalFeature } from 'features/Modules/SearchModal/SearchModalFeature';
import getConfig from 'next/config';
import React, { useState } from 'react';
import { LayoutStyle } from '../Layout/LayoutFeature';

export interface HeaderFeatureProps {
	siteMobileNavigationItems: Content.Link[];
	siteNavigationItems: Navigation.SiteNavigationItem[];
	layout?: LayoutStyle;
}

export const HeaderFeature = ({ siteMobileNavigationItems, siteNavigationItems, layout }: HeaderFeatureProps) => {
	const siteContext = useSiteContext();
	const pageContext = usePageContext();
	const dictionary = useDictionaryContext();
	const { publicRuntimeConfig } = getConfig();

	const { CONTENT_API_URL: cmsUrl, APP_ENV: appEnv } = publicRuntimeConfig ?? {};

	const [showSearch, setShowSearch] = useState(false);

	const [modalTrigger, setModalTrigger] = useState<HTMLElement>();
	const extraBurgerLinks: NavigationItemProps[] = siteContext?.settings?.globalLinks?.additionalBurgerLinks?.map((extraBurgerLink) => {
		const burgerLinkPage = extraBurgerLink?.url !== null ? extraBurgerLink?.url : extraBurgerLink?.route.path;
		const itemIsCurrentPage = pageContext?.url === burgerLinkPage;
		return {
			...extraBurgerLink,
			name: extraBurgerLink.title,
			current: itemIsCurrentPage,
			link: (
				<LinkFeature url={extraBurgerLink.url} route={extraBurgerLink.route} title={extraBurgerLink.title} currentPage={itemIsCurrentPage} />
			),
		};
	});

	const mobileNavigationLinks: NavigationItemProps[] = siteMobileNavigationItems?.map((navigationItem) => {
		const navigationItemPage = navigationItem?.url !== null ? navigationItem?.url : navigationItem?.route.path;
		const itemIsCurrentPage = pageContext?.url === navigationItemPage;
		return {
			...navigationItem,
			name: navigationItem.title,
			current: itemIsCurrentPage,
			link: (
				<LinkFeature
					url={navigationItem.url}
					route={navigationItem.route}
					target={navigationItem.target}
					title={navigationItem.title}
					currentPage={itemIsCurrentPage}
				/>
			),
		};
	});

	const { searchHeader, searchEnabled } = siteContext?.settings?.searchConfiguration ?? {};

	const openSearchModal = (e: React.MouseEvent) => {
		if (!(e.target instanceof HTMLElement)) return;

		setModalTrigger(e.target);
		setShowSearch(true);
		e.preventDefault();
	};

	const closeSearchModal = () => {
		setShowSearch(false);
		setTimeout(() => modalTrigger?.focus(), 0);
	};

	return pageContext.documentType === 'closableHelpPage' || layout === 'simple' || layout === 'simpleNoClose' ? (
		<HeaderSimple closeText={layout !== 'simpleNoClose' ? dictionary.getAction('Close') : null} logo={<LogoFeature />} />
	) : (
		<Header
			topArea={appEnv === 'dev' || appEnv === 'test' ? <HeaderTop>{appEnv.toUpperCase() + ' ' + cmsUrl.replace(/\D/g, '')}</HeaderTop> : null}
			heading={dictionary.getExplainer(siteContext?.theme)}
			logo={<LogoFeature />}
			darkLogo={<LogoFeature dark />}
			enforcedFullLogo={<LogoFeature dark enforceFullLogo />}
			mobileNavigationLinks={mobileNavigationLinks}
			navItems={
				siteNavigationItems?.length > 0
					? siteNavigationItems.map((item) => {
							const { link, title, subLinks } = item ?? {};
							const singleLink = link?.[0];
							const itemIsActive = pageContext?.url?.startsWith(singleLink?.route?.path);
							const itemIsCurrentPage = pageContext?.url === singleLink?.url;
							const { title: name } = singleLink ?? {};
							return {
								target: singleLink?.target,
								name: title ? title : name,
								link: <LinkFeature {...singleLink} currentPage={itemIsCurrentPage} />,
								current: itemIsActive,
								descendants:
									subLinks?.length > 0
										? subLinks?.map((link) => {
												const itemIsActive = pageContext.url === link.url;
												return {
													name: link.title,
													url: link?.url,
													link: <LinkFeature {...link} currentPage={itemIsActive} />,
													current: itemIsActive,
												};
										  })
										: null,
							};
					  })
					: []
			}
			extraBurgerLinks={extraBurgerLinks}
			burgerButtonText={dictionary.getLabel('Menu')}
			goToMainText={dictionary.getAction('GoToContent')}
			searchButtonOnClick={searchEnabled && openSearchModal}
			searchButtonText={dictionary.getSearch('Search')}
			skipToMainText={dictionary.getServiceInfo('SkipToMain')}
			openText={dictionary.getAction('Open')}
			closeText={dictionary.getAction('Close')}
			authentication={<LoginFeature />}
		>
			<SearchModalFeature searchHeader={searchHeader} show={showSearch} onClose={closeSearchModal} />
		</Header>
	);
};
